import { Box, Card, CardContent, CardHeader, Skeleton, Typography } from "@mui/material"
import type { JSX, ReactNode } from "react"

import { Pagination } from "@/components/ui/pagination"
import { SearchInput } from "@crm-src/components/search-input"

interface ResellerNetworkColumnProps<T> {
  title: string
  icon: ReactNode
  entityNoun: string
  emptyMessage: string
  /** Rendered to the right of the title — "Link Customer", "Add Dealer". */
  action?: ReactNode
  list: {
    rows: T[]
    totalCount: number
    totalPages: number
    page: number
    perPage: number
    search: string
    loading: boolean
    error: string | null
    setPage: (page: number) => void
    setSearch: (search: string) => void
  }
  renderRow: (row: T) => ReactNode
  /** Explicitly `| undefined`: the repo runs `exactOptionalPropertyTypes`, so
   * a caller passing a possibly-undefined value would not satisfy a bare
   * optional. */
  "data-cy"?: string | undefined
}

/**
 * ONE column of the Network tab: a search box, a list, and a pager, all bound
 * to their OWN query state.
 *
 * The three columns (Dealers / Customers / Prospects) sit side by side, so this
 * is deliberately narrow-friendly: no table, no horizontal rules, rows that
 * wrap. TEN per page rather than the app-wide 25, because 25 rows in a
 * third-width column is a scroll, not a page — and the pager keeps its own
 * count, so "Showing 1–10 of 63 customers" answers the question the column is
 * really being asked.
 *
 * Presentational: it owns no fetching. The parent hands it a
 * `useResellerPagedList` result, which is what makes the columns independent.
 */
export function ResellerNetworkColumn<T extends { id: string }>({
  title,
  icon,
  entityNoun,
  emptyMessage,
  action,
  list,
  renderRow,
  "data-cy": dataCy,
}: ResellerNetworkColumnProps<T>): JSX.Element {
  return (
    // Both attributes: Cypress selects on `data-cy`, Testing Library on
    // `data-testid`. Emitting one and querying the other is a whole class of
    // "the element is right there" test failure.
    <Card data-cy={dataCy} data-testid={dataCy} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CardHeader
        sx={{ pb: 0 }}
        title={
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
              <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
              <span>{title}</span>
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 400, color: "text.secondary" }}>
                ({list.totalCount})
              </Typography>
            </Box>
            {action}
          </Box>
        }
      />
      <CardContent sx={{ pt: 2, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <SearchInput
          placeholder={`Search ${entityNoun}…`}
          searchQuery={list.search}
          onSearch={list.setSearch}
          onClear={() => list.setSearch("")}
        />

        {list.loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={64} />
            ))}
          </Box>
        ) : list.error ? (
          <Box sx={{ textAlign: "center", color: "error.main", py: 4, bgcolor: "surfaceSoft", borderRadius: 1 }}>
            <Typography sx={{ fontSize: 14 }}>{list.error}</Typography>
          </Box>
        ) : list.rows.length === 0 ? (
          <Box sx={{ textAlign: "center", color: "text.secondary", py: 4, bgcolor: "surfaceSoft", borderRadius: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}>{icon}</Box>
            <Typography sx={{ fontSize: 14 }}>
              {list.search ? `No ${entityNoun} match “${list.search}”.` : emptyMessage}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
            {list.rows.map((row) => (
              <Box key={row.id}>{renderRow(row)}</Box>
            ))}
          </Box>
        )}

        <Pagination
          page={list.page}
          perPage={list.perPage}
          totalCount={list.totalCount}
          totalPages={list.totalPages}
          onPageChange={list.setPage}
          entityNoun={entityNoun}
          hideIfSinglePage
        />
      </CardContent>
    </Card>
  )
}
